import * as React from "react";
import Box from "@mui/material/Box";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import {TableFooter} from "@mui/material";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import EnhancedTableHeaderRow, {HeadCellConfig} from "./EnhancedTableHeaderRow";
import EnhancedTableBody, {EmptyRows} from "./EnhancedTableBody";

/**
 * Converts a TableState object into http query params for use in API requests.
 * @param tableState
 */
export const tableStateToParams = (tableState: TableState): string => {
    const sortDir = tableState.order === 'desc' ? '-' : '';
    let queryParams = `?p=${tableState.page}&page_size=${tableState.pageSize}&ordering=${sortDir}${tableState.orderBy}`;
    if (tableState.searchString) {
        queryParams += `&search=${tableState.searchString}`;
    }
    return queryParams
}

export interface TableState {
    page: number;
    pageSize: number;
    orderBy: string;
    order: 'asc' | 'desc';
    searchString: string;
}

interface EnhancedTableProps {
    loadingState: 'loading' | 'error' | 'success';
    backgroundLoading?: boolean;
    columns: HeadCellConfig[];
    rows: object[];
    rowCount: number;
    tableState: TableState;
    stateHandler: {
        (newState: TableState): void;
    };
    canEdit?: boolean;
    editHandler?: {
        (rowToEdit: any): void;
    };
    canDelete?: boolean;
    deleteHandler?: {
        (rowToEdit: any): void;
    };
    favoriteHandler?: {
        (rowToggled: any): void;
    };
    allowAllRows?: boolean;
}

export default (props: EnhancedTableProps) => {
    const handlePageChange = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        props.stateHandler({
            ...props.tableState,
            page: newPage + 1
        });
    };

    const handlePageSizeChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        props.stateHandler({
            ...props.tableState,
            page: 1,
            pageSize: parseInt(event.target.value, 10)
        });
    };

    const handleSortChange = (
        event: React.MouseEvent<unknown>,
        property: any,
    ) => {
        const isAsc = props.tableState.orderBy === property && props.tableState.order === 'asc';
        props.stateHandler({
            ...props.tableState,
            order: isAsc ? 'desc' : 'asc',
            orderBy: property,
            page: 1
        });
    };

    const handleSearch = async (newSearch: string) => {
        props.stateHandler({
            ...props.tableState,
            searchString: newSearch,
            page: 1
        });
    };

    const pageSizes: (number | { value: number, label: string })[] = [10, 25, 50];
    if (props.allowAllRows) {
        pageSizes.push({value: -1, label: 'All'})
    }

    return (
        <Box>
            <TableContainer component={Paper}>
                <Table sx={{minWidth: 750}} aria-labelledby="tableTitle">
                    <EnhancedTableHeaderRow
                        headers={props.columns}
                        backgroundLoading={props.backgroundLoading}
                        order={props.tableState.order}
                        orderBy={props.tableState.orderBy}
                        onSortChange={handleSortChange}
                        handleSearch={handleSearch}
                        canEdit={props.canEdit === true}
                    />
                    <EnhancedTableBody
                        loadingState={props.loadingState}
                        columns={props.columns}
                        rows={props.rows}
                        pageSize={props.tableState.pageSize}
                        headers={props.columns}
                        canEdit={props.canEdit === true}
                        editHandler={props.editHandler}
                        canDelete={props.canDelete === true}
                        deleteHandler={props.deleteHandler}
                        favoriteHandler={props.favoriteHandler}
                    />
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={pageSizes}
                                count={props.rowCount || 0}
                                page={props.tableState.page - 1}
                                onPageChange={handlePageChange}
                                rowsPerPage={props.tableState.pageSize}
                                onRowsPerPageChange={handlePageSizeChange}/>
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
        ;
}

